/**
 * Offline Database — IndexedDB via Dexie
 *
 * v1: api_cache + sync_queue (Phase 0 — caching + queueing)
 * v2: + local entity tables (Phase 1 — Offline-First)
 *
 * الفلسفة: الجهاز هو مصدر القراءة الأساسي.
 * كل عملية تُحفظ محلياً فوراً + تدخل قائمة المزامنة لرفعها للسيرفر لاحقاً.
 */
import Dexie from 'dexie';

export const offlineDB = new Dexie('NazarioOfflineDB');

// v1 (الموجود سابقاً)
offlineDB.version(1).stores({
  api_cache: 'key, updatedAt',
  sync_queue: '++id, endpoint, method, createdAt, status',
  meta: 'key'
});

// v2: جداول الكيانات المحلية + خريطة ID
offlineDB.version(2).stores({
  api_cache: 'key, updatedAt',
  sync_queue: '++id, endpoint, method, createdAt, status, localId, entityType',
  meta: 'key',
  // كيانات محلية — id قد يكون offline_xxx (لم يُزامن) أو real_uuid (مُزامن)
  local_customers: 'id, name, phone, _offline_pending, _local_created_at',
  local_products: 'id, barcode, name, _offline_pending, _local_created_at',
  local_sales: 'id, customer_id, created_at, _offline_pending, _local_created_at',
  local_payments: 'id, customer_id, supplier_id, type, _offline_pending, _local_created_at',
  local_suppliers: 'id, name, phone, _offline_pending, _local_created_at',
  local_purchases: 'id, supplier_id, _offline_pending, _local_created_at',
  // خريطة من ID المحلي المؤقت إلى ID السيرفر الحقيقي بعد المزامنة
  id_map: 'localId, realId, entityType, mappedAt',
});

// ============ api_cache ============
export async function cacheGet(url, data) {
  try {
    await offlineDB.api_cache.put({ key: url, data, updatedAt: Date.now() });
  } catch (e) { console.warn('[Offline] cacheGet failed:', e); }
}
export async function getCached(url) {
  try {
    const entry = await offlineDB.api_cache.get(url);
    return entry ? entry.data : null;
  } catch { return null; }
}

// ============ sync_queue ============
export async function enqueueSync(endpoint, method, payload, meta = {}) {
  const item = {
    endpoint, method, payload, meta,
    createdAt: Date.now(),
    status: 'pending',
    attempts: 0,
    lastError: null,
    localId: meta.localId || null,
    entityType: meta.entityType || null,
  };
  const id = await offlineDB.sync_queue.add(item);
  return { id, ...item };
}
export async function getPendingQueue() {
  return await offlineDB.sync_queue
    .where('status').anyOf(['pending', 'failed'])
    .sortBy('createdAt');
}
export async function updateQueueItem(id, changes) {
  await offlineDB.sync_queue.update(id, changes);
}
export async function removeQueueItem(id) {
  await offlineDB.sync_queue.delete(id);
}
export async function getPendingCount() {
  return await offlineDB.sync_queue
    .where('status').anyOf(['pending', 'failed']).count();
}

// ============ id_map (Reconciliation) ============
export async function mapLocalToReal(localId, realId, entityType) {
  await offlineDB.id_map.put({ localId, realId, entityType, mappedAt: Date.now() });
}
export async function resolveId(maybeLocalId) {
  if (!maybeLocalId || typeof maybeLocalId !== 'string') return maybeLocalId;
  if (!maybeLocalId.startsWith('offline_')) return maybeLocalId;
  const m = await offlineDB.id_map.get(maybeLocalId);
  return m ? m.realId : maybeLocalId;
}

// ============ مسح الكاش (logout) ============
export async function clearOfflineData() {
  await offlineDB.api_cache.clear();
  await offlineDB.sync_queue.clear();
  await offlineDB.local_customers.clear();
  await offlineDB.local_products.clear();
  await offlineDB.local_sales.clear();
  await offlineDB.local_payments.clear();
  await offlineDB.local_suppliers.clear();
  await offlineDB.local_purchases.clear();
  await offlineDB.id_map.clear();
}
